import { create } from 'zustand';
import { configDb, type DashboardMode } from '@/persistence/config-db';

/**
 * Active dashboard mode (D-020). Persisted to the Dexie `preferences`
 * table and rehydrated on boot via {@link initModeStore}.
 *
 * `mode` is the source of truth for which slice of the dataset's
 * widgets / layouts / pinned filters is rendered. Switching modes
 * triggers reloads in the dependent stores.
 */

const PREF_KEY = 'mode';

type State = {
  mode: DashboardMode;
  setMode: (next: DashboardMode) => Promise<void>;
};

export const useModeStore = create<State>((set) => ({
  mode: 'normal',
  setMode: async (next) => {
    set({ mode: next });
    try {
      await configDb.preferences.put({ key: PREF_KEY, value: next });
    } catch {
      // Non-fatal: mode reverts to default on next reload.
    }
  },
}));

/** Read the persisted mode from Dexie. Used by the boot path in main.tsx. */
export async function initModeStore(): Promise<void> {
  try {
    const pref = await configDb.preferences.get(PREF_KEY);
    if (pref?.value === 'normal' || pref?.value === 'compare') {
      useModeStore.setState({ mode: pref.value });
    }
  } catch {
    // Non-fatal — keep the default.
  }
}
